import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { pnidApi } from '../api';
import type { TagSuggestion, TagSuggestionKind } from '../types';
import { hyphenatedTag } from '../utils/hyphenatedTag';
import { MINIMUM_QUERY_LENGTH, SUGGESTION_DEBOUNCE_MS } from '../utils/tagSuggestionLookup';

const SUGGESTION_LIMIT = 20;

/** The dropdown row that keeps the typed text as a new tag instead of adopting one. */
const CREATE_ROW_INDEX = -2;
const NO_HIGHLIGHT_INDEX = -1;

/** Suggestions, and the exact query text they answer. */
interface SuggestionResult {
  query: string;
  suggestions: TagSuggestion[];
}

const EMPTY_RESULT: SuggestionResult = { query: '', suggestions: [] };

export interface TagAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * The tag the reviewer has settled on, once per settling rather than per
   * keystroke: picking a suggestion, keeping the typed text, or leaving the
   * field. The value is what the field holds after any adoption, so a caller
   * that acts on the chosen tag never acts on a half-typed one.
   */
  onSettle?: (value: string) => void;
  /** The drawing whose domain is searched. Without one the input is a plain text box. */
  docId: string | null | undefined;
  kind: TagSuggestionKind;
  placeholder?: string;
  /** Names the field where no visible label sits beside it, such as a table cell. */
  ariaLabel?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Drops the input's own padding and border so a caller's class owns the look.
   * The field's own inline style would otherwise outrank any stylesheet rule,
   * which a hover- or focus-styled cell needs.
   */
  unstyled?: boolean;
  /**
   * Rewrites a tag written with a space between its letters and its number
   * (`TE 603`) to its hyphen spelling (`TE-603`) when the reviewer commits it —
   * picking a suggestion, keeping the typed text, or leaving the field — and
   * says so under the field.
   */
  hyphenateSpacedTag?: boolean;
}

/** The suggestion whose tag the typed text already names, once separators are normalized. */
function normalizedMatchOf(result: SuggestionResult, typed: string): TagSuggestion | null {
  if (result.query !== typed) return null;
  return result.suggestions.find((suggestion) => suggestion.normalized_match) ?? null;
}

/** Normalized matches first, so the tag the reviewer is re-typing leads the list. */
function pinNormalizedMatchesFirst(suggestions: TagSuggestion[]): TagSuggestion[] {
  const matches = suggestions.filter((suggestion) => suggestion.normalized_match);
  if (matches.length === 0) return suggestions;
  return [...matches, ...suggestions.filter((suggestion) => !suggestion.normalized_match)];
}

/** A tag input that suggests the tags the drawing's domain already uses.
 *
 *  Selecting a suggestion, or leaving the field while the typed text names an
 *  existing tag under a different separator, rewrites the value to that domain's
 *  spelling of the tag. With `hyphenateSpacedTag`, a spaced spelling then takes its
 *  hyphen form. Nothing is rewritten while the reviewer types. */
const TagAutocompleteInput: React.FC<TagAutocompleteInputProps> = ({
  value,
  onChange,
  onSettle,
  docId,
  kind,
  placeholder,
  ariaLabel,
  disabled = false,
  required = false,
  className,
  style,
  unstyled = false,
  hyphenateSpacedTag = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The hyphen spelling the last commit wrote, shown under the field while it still holds it.
  const [writtenAsTag, setWrittenAsTag] = useState<string | null>(null);
  const writtenAsHintId = useId();
  const [isFocused, setIsFocused] = useState(false);
  const [result, setResult] = useState<SuggestionResult>(EMPTY_RESULT);
  const [highlightIndex, setHighlightIndex] = useState(NO_HIGHLIGHT_INDEX);

  // The blur handler reads the freshest suggestions, not the ones captured when
  // it was bound, so an in-flight fetch that lands during blur still counts.
  const resultRef = useRef<SuggestionResult>(EMPTY_RESULT);
  resultRef.current = result;
  // Selecting a row moves focus out of the input; the resulting blur must not
  // then re-adopt over the reviewer's explicit choice.
  const justSelectedRef = useRef(false);

  const typed = value ?? '';
  const trimmedQuery = typed.trim();
  const meetsMinimumLength = trimmedQuery.length >= MINIMUM_QUERY_LENGTH[kind];
  const canSuggest = Boolean(docId) && !disabled;

  useEffect(() => {
    if (!canSuggest || !isFocused || !meetsMinimumLength) {
      setResult(EMPTY_RESULT);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      pnidApi
        .getTagSuggestions(docId as string, {
          kind,
          query: trimmedQuery,
          limit: SUGGESTION_LIMIT,
          signal: controller.signal,
        })
        .then((suggestions) => setResult({ query: typed, suggestions }))
        // A failed lookup leaves the reviewer with a plain text field, which is
        // the pre-suggestion behaviour and still saves.
        .catch(() => undefined);
    }, SUGGESTION_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [canSuggest, docId, isFocused, kind, meetsMinimumLength, trimmedQuery, typed]);

  const suggestions = useMemo(
    () => (result.query === typed ? pinNormalizedMatchesFirst(result.suggestions) : []),
    [result, typed],
  );
  const normalizedMatch = normalizedMatchOf(result, typed);
  // Below the minimum length nothing is looked up, so there is nothing to offer —
  // not even the create row, which would otherwise be a dropdown on the first keystroke.
  const showCreateRow = canSuggest && meetsMinimumLength && trimmedQuery.length > 0 && !normalizedMatch;
  const dropdownOpen = isOpen && (suggestions.length > 0 || showCreateRow);
  const showWrittenAs = writtenAsTag !== null && writtenAsTag === typed;

  // A row can vanish under the highlight when a slower lookup lands; drop the
  // highlight rather than let Enter act on a row that is no longer shown.
  useEffect(() => {
    setHighlightIndex((current) => {
      if (current === CREATE_ROW_INDEX) return showCreateRow ? current : NO_HIGHLIGHT_INDEX;
      if (current >= suggestions.length) return NO_HIGHLIGHT_INDEX;
      return current;
    });
  }, [showCreateRow, suggestions.length]);

  /**
   * The spelling the field commits for `tag`: with `hyphenateSpacedTag`, a spaced
   * tag's hyphen spelling, noted under the field; otherwise `tag`. Every commit —
   * a pick, the create row, leaving the field — goes through here.
   */
  const committedSpelling = useCallback((tag: string) => {
    const hyphenated = hyphenateSpacedTag ? hyphenatedTag(tag) : null;
    setWrittenAsTag(hyphenated);
    return hyphenated ?? tag;
  }, [hyphenateSpacedTag]);

  const selectSuggestion = useCallback(
    (suggestion: TagSuggestion) => {
      justSelectedRef.current = true;
      const committed = committedSpelling(suggestion.tag);
      onChange(committed);
      onSettle?.(committed);
      setIsOpen(false);
      setHighlightIndex(NO_HIGHLIGHT_INDEX);
    },
    [committedSpelling, onChange, onSettle],
  );

  /** Keep the typed text as a tag the domain does not have yet. */
  const keepTypedText = useCallback(() => {
    justSelectedRef.current = true;
    const committed = committedSpelling(typed);
    if (committed !== typed) onChange(committed);
    onSettle?.(committed);
    setIsOpen(false);
    setHighlightIndex(NO_HIGHLIGHT_INDEX);
  }, [committedSpelling, onChange, onSettle, typed]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (!dropdownOpen) return;
      event.stopPropagation();
      setIsOpen(false);
      setHighlightIndex(NO_HIGHLIGHT_INDEX);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!dropdownOpen) {
        setIsOpen(true);
        return;
      }
      event.preventDefault();
      const rows = [
        ...suggestions.map((_, index) => index),
        ...(showCreateRow ? [CREATE_ROW_INDEX] : []),
      ];
      if (rows.length === 0) return;
      const current = rows.indexOf(highlightIndex);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = current === -1
        ? (step === 1 ? 0 : rows.length - 1)
        : (current + step + rows.length) % rows.length;
      setHighlightIndex(rows[next]);
      return;
    }

    if (event.key === 'Enter') {
      // With the dropdown closed, Enter belongs to the surrounding form and saves.
      // While it is open Enter belongs to the dropdown: it takes the highlighted
      // row, or — with nothing highlighted — just dismisses the list, so a first
      // Enter over an open dropdown never saves a half-typed tag.
      if (!dropdownOpen) return;
      event.preventDefault();
      if (highlightIndex === NO_HIGHLIGHT_INDEX) {
        setIsOpen(false);
        return;
      }
      if (highlightIndex === CREATE_ROW_INDEX) keepTypedText();
      else selectSuggestion(suggestions[highlightIndex]);
      return;
    }

    if (event.key === 'Tab') {
      // Tab commits what is typed and moves on, so the same adoption a blur does
      // applies — the blur handler runs right after and does exactly that.
      setIsOpen(false);
      setHighlightIndex(NO_HIGHLIGHT_INDEX);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={typed}
        onChange={(event) => {
          justSelectedRef.current = false;
          setWrittenAsTag(null);
          onChange(event.target.value);
          setIsOpen(true);
          setHighlightIndex(NO_HIGHLIGHT_INDEX);
        }}
        onFocus={() => {
          setIsFocused(true);
          setIsOpen(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          setIsOpen(false);
          setHighlightIndex(NO_HIGHLIGHT_INDEX);
          // A row that was just chosen has already settled; re-reporting it here
          // would say the reviewer settled twice on the one choice.
          if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
          }
          // Only a caller that takes the settled tag adopts the domain's spelling here.
          const adopted = onSettle ? normalizedMatchOf(resultRef.current, typed)?.tag ?? typed : typed;
          const committed = committedSpelling(adopted);
          if (committed !== typed) onChange(committed);
          onSettle?.(committed);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={showWrittenAs ? writtenAsHintId : undefined}
        disabled={disabled}
        required={required}
        className={className}
        autoComplete="off"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          ...(unstyled ? {} : inputStyle),
          ...style,
        }}
      />

      {/* Hidden from the accessible name of a label that wraps the field; the input
          reads it as its description instead. */}
      {showWrittenAs && (
        <div id={writtenAsHintId} aria-hidden="true" style={writtenAsHintStyle}>
          Written as {writtenAsTag}
        </div>
      )}

      {dropdownOpen && (
        <div style={dropdownStyle}>
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.tag}
              // mousedown fires before blur, so the row is chosen rather than
              // lost to the field closing under the pointer.
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setHighlightIndex(index)}
              style={{
                ...rowStyle,
                background: highlightIndex === index ? '#eff6ff' : 'transparent',
              }}
            >
              <div style={tagStyle}>{suggestion.tag}</div>
              <div style={descriptionStyle}>{suggestion.description || '—'}</div>
              {suggestion.normalized_match && (
                <div style={normalizedNoteStyle}>matches existing {suggestion.tag}</div>
              )}
            </div>
          ))}

          {showCreateRow && (
            <div
              onMouseDown={(event) => {
                event.preventDefault();
                keepTypedText();
              }}
              onMouseEnter={() => setHighlightIndex(CREATE_ROW_INDEX)}
              style={{
                ...rowStyle,
                borderTop: suggestions.length > 0 ? '1px solid #e5e7eb' : undefined,
                background: highlightIndex === CREATE_ROW_INDEX ? '#f0fdf4' : 'transparent',
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 500, color: '#166534' }}>
                Create “{trimmedQuery}”
              </div>
              <div style={descriptionStyle}>New tag for this plant</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  padding: 8,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontSize: 12,
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  maxHeight: 220,
  overflowY: 'auto',
  zIndex: 1000,
};

const rowStyle: React.CSSProperties = {
  padding: '6px 10px',
  cursor: 'pointer',
};

const tagStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  fontWeight: 600,
  color: '#111827',
};

const descriptionStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#6b7280',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const writtenAsHintStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 400,
  color: 'var(--raven-text-secondary)',
};

const normalizedNoteStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#0369a1',
  marginTop: 2,
};

export default TagAutocompleteInput;
